import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, ShoppingBag, Home } from 'lucide-react';
import confetti from 'canvas-confetti';

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const reference = searchParams.get('reference') || searchParams.get('trxref');
  const status = searchParams.get('status');

  // Optional: Fire confetti animation on mount
  useEffect(() => {
    if (status === 'success' || reference) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
    const timer = setTimeout(() => {
      navigate('/transactions');
    }, 9000);

    return () => clearTimeout(timer);
  }, [navigate, status, reference]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden text-center p-8">
        {/* Success Icon */}
        <div className="mx-auto mb-6 flex items-center justify-center w-24 h-24 rounded-full bg-green-100">
          <CheckCircle className="w-16 h-16 text-green-600" />
        </div>

        {/* Main Message */}
        <h1 className="text-3xl font-bold text-gray-900 mb-3">
          Payment Successful!
        </h1>
        
        <p className="text-gray-600 mb-8">
          Thank you for your purchase. Your transaction has been completed successfully.
          {reference && (
            <span className="block mt-2 text-sm text-gray-500">
              Reference: <strong>{reference}</strong>
            </span>
          )}
        </p>

        {/* What happens next */}
        <div className="bg-green-50 rounded-xl p-6 mb-8 text-left">
          <h3 className="font-semibold text-green-800 mb-3">What's next?</h3>
          <ul className="space-y-3 text-sm text-gray-700">
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>You'll receive an order confirmation email shortly</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>Your items will be processed immediately</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>You can track your order in your dashboard</span>
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4">
          <button
            onClick={() => navigate('/transactions')}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <ShoppingBag size={20} />
            View My Orders
          </button>

          <button
            onClick={() => navigate('/market')}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <Home size={20} />
            Continue Shopping
          </button>
        </div>

        {/* Small footer text */}
        <p className="mt-8 text-sm text-gray-500">
          Transaction was processed securely via Paystack
          {reference && ` • Ref: ${reference.slice(0, 8)}...`}
        </p>

        {/* Auto-redirect notice */}
        <p className="mt-4 text-xs text-gray-400">
          Redirecting to your orders in a few seconds...
        </p>
      </div>
    </div>
  );
}